import React, { useState } from "react";
import {
  Key,
  Shield,
  User,
  Clock,
  CreditCard,
  Plus,
  RefreshCw,
  Lock,
  Database,
  Search,
  Filter,
  Calendar,
  Copy,
  Check,
  Tag,
  Trash2,
} from "lucide-react";
import { useDashboard } from "../context/DashboardContext";
import "./Licenses.css";

const Licenses = () => {
  const {
    roleLabel,
    canCreateKeys,
    groupedLicenses,
    loadingLicenses,
    copiedKey,
    handleCopyKey,
    handleCreateLicense,
    handleDeleteLicense,
    handleHasUsedChange,
    formatISTDateTime,
    isExpired,
  } = useDashboard();

  const [name, setName] = useState("");
  const [duration, setDuration] = useState(7);
  const [payment, setPayment] = useState("");
  const [loading, setLoading] = useState(false);
  const [showExpiredOnly, setShowExpiredOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const onSubmitLicense = async (e) => {
    e.preventDefault();
    if (!name.trim() || !payment.trim()) {
      alert("Please fill in client name and payment reference.");
      return;
    }

    setLoading(true);
    try {
      await handleCreateLicense({
        name: name.trim(),
        duration,
        payment: payment.trim(),
      });
      setName("");
      setDuration(7);
      setPayment("");
    } catch {
      // Error handled by notify in context
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bf-licenses-container">
      {/* Header Banner */}
      <section className="bf-licenses-header">
        <div>
          <h1 className="bf-licenses-title">
            <Key size={22} color="var(--green-0)" />
            <span>CRYPTOGRAPHIC LICENSE INVENTORY & GENERATOR</span>
          </h1>
          <p className="bf-licenses-subtitle">
            Generate HMAC-backed license keys, configure evaluation windows, and track client activation states.
          </p>
        </div>

        <div className="bf-operator-pill">
          <span className="tech-label">MINTING CLEARANCE:</span>
          <span
            className="tech-badge"
            style={{
              color: canCreateKeys ? "var(--green-0)" : "var(--amber)",
              background: canCreateKeys ? "var(--green-bg)" : "var(--amber-bg)",
              borderColor: canCreateKeys ? "var(--green-border)" : "var(--amber-border)",
            }}
          >
            {canCreateKeys ? "AUTHORIZED" : "RESTRICTED"}
          </span>
        </div>
      </section>

      {/* License Generator Section */}
      {canCreateKeys ? (
        <section className="bf-panel" id="create-license-panel">
          <div className="bf-panel-header">
            <div className="bf-panel-title">
              <Key size={16} color="var(--green-0)" />
              <span>PROVISION NEW LICENSE KEY</span>
            </div>
            <span className="tech-badge green">AES-256 HMAC ENCRYPTED</span>
          </div>

          <form onSubmit={onSubmitLicense} className="bf-form-row">
            <div className="bf-form-group">
              <label className="tech-label" htmlFor="lic-client-name">
                <User size={12} />
                RECIPIENT / CLIENT NAME
              </label>
              <input
                id="lic-client-name"
                className="bf-input-ctrl font-mono"
                type="text"
                placeholder="e.g. John Doe / AlphaCorp"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="bf-form-group">
              <label className="tech-label" htmlFor="lic-plan-duration">
                <Clock size={12} />
                PLAN DURATION
              </label>
              <select
                id="lic-plan-duration"
                className="bf-select-ctrl font-mono"
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
              >
                <option value="7">7 Days [Evaluation]</option>
                <option value="15">15 Days [Standard]</option>
                <option value="30">30 Days [Full Month]</option>
              </select>
            </div>

            <div className="bf-form-group">
              <label className="tech-label" htmlFor="lic-payment-ref">
                <CreditCard size={12} />
                PAYMENT / INVOICE REF
              </label>
              <input
                id="lic-payment-ref"
                className="bf-input-ctrl font-mono"
                type="text"
                placeholder="e.g. UPI-9842 / Paid"
                value={payment}
                onChange={(e) => setPayment(e.target.value)}
                required
              />
            </div>

            <button type="submit" className="bf-btn-action" disabled={loading}>
              {loading ? (
                <>
                  <RefreshCw size={15} className="status-dot pulse" />
                  MINTING KEY...
                </>
              ) : (
                <>
                  <Plus size={16} strokeWidth={2.5} />
                  ISSUE LICENSE
                </>
              )}
            </button>
          </form>
        </section>
      ) : (
        <section className="bf-panel" id="create-license-panel">
          <div className="bf-panel-header">
            <div className="bf-panel-title">
              <Lock size={16} color="var(--amber)" />
              <span>KEY ISSUANCE CLEARANCE RESTRICTED</span>
            </div>
            <span className="tech-badge amber">SECURITY CLEARANCE</span>
          </div>
          <div className="bf-restricted-notice">
            <Shield size={20} color="var(--amber)" style={{ flexShrink: 0, marginTop: "2px" }} />
            <p className="bf-restricted-text">
              Your clearance level <strong>[{roleLabel}]</strong> does not possess direct key minting authority.
              Cryptographic key generation is restricted to <strong>Controllers</strong> and <strong>Sellers</strong>.
              You have read-only inspection clearance for keys in your operational downline.
            </p>
          </div>
        </section>
      )}

      {/* License Inventory Console */}
      <section className="bf-panel" id="list-section">
        <div className="bf-panel-header">
          <div className="bf-panel-title">
            <Database size={16} color="var(--green-0)" />
            <span>LICENSE INVENTORY CONSOLE</span>
          </div>

          <div className="bf-filter-bar">
            <div className="bf-search-box">
              <Search size={14} color="var(--text-3)" />
              <input
                type="text"
                placeholder="Search recipient, key, payment, or issuer..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="bf-filter-group">
              <button
                className={`bf-toggle-btn ${showExpiredOnly ? "active" : ""}`}
                onClick={() => setShowExpiredOnly((prev) => !prev)}
              >
                <Filter size={13} />
                <span>{showExpiredOnly ? "SHOW ALL RECORDS" : "EXPIRED ONLY"}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Grouped Batches by IST Date */}
        {loadingLicenses ? (
          <div className="bf-empty-state">
            <div className="status-dot green pulse" style={{ width: "12px", height: "12px" }} />
            <span>SYNCING LICENSE DATABASE FROM CLUSTER...</span>
          </div>
        ) : Object.entries(groupedLicenses).length === 0 ? (
          <div className="bf-empty-state">
            <Database size={36} strokeWidth={1.5} color="var(--text-3)" />
            <span>NO LICENSE RECORDS FOUND IN CLUSTER STORAGE</span>
          </div>
        ) : (
          Object.entries(groupedLicenses)
            .sort((a, b) => {
              const parseDate = (str) => {
                const [d, m, y] = str.split("/").map(Number);
                return new Date(y, m - 1, d);
              };
              return parseDate(b[0]) - parseDate(a[0]);
            })
            .map(([date, items]) => {
              const filteredItems = items
                .filter((item) => (showExpiredOnly ? isExpired(item.expiryDate) : true))
                .filter((item) => {
                  if (!searchQuery.trim()) return true;
                  const query = searchQuery.toLowerCase();
                  return (
                    item.name?.toLowerCase().includes(query) ||
                    item.licenseKey?.toLowerCase().includes(query) ||
                    item.genBy?.toLowerCase().includes(query) ||
                    item.payment?.toLowerCase().includes(query)
                  );
                });

              if (filteredItems.length === 0) return null;

              return (
                <div className="bf-batch-group" key={date}>
                  <div className="bf-batch-header">
                    <Calendar size={13} color="var(--green-0)" />
                    <span>{"//"} BATCH: {date}</span>
                    <span style={{ color: "var(--text-3)", fontSize: "10.5px" }}>
                      ({filteredItems.length} records)
                    </span>
                  </div>

                  <div className="bf-table-container">
                    <table className="bf-data-table">
                      <thead>
                        <tr>
                          <th>RECIPIENT</th>
                          <th>LICENSE KEY</th>
                          <th>PLAN</th>
                          <th>EXPIRY (IST)</th>
                          <th>STATUS</th>
                          <th>PAYMENT</th>
                          <th>ISSUED BY</th>
                          <th>ISSUED AT</th>
                          <th style={{ textAlign: "right" }}>ACTIONS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredItems.map((item) => {
                          const expired = isExpired(item.expiryDate);
                          const isCopied = copiedKey === item.licenseKey;

                          return (
                            <tr key={item._id} className={expired ? "row-expired" : ""}>
                              <td>
                                <span style={{ fontWeight: 600, color: "var(--text-0)" }}>
                                  {item.name}
                                </span>
                              </td>
                              <td>
                                <div className="bf-key-cell">
                                  <span>{item.licenseKey}</span>
                                  <button
                                    className={`bf-copy-btn ${isCopied ? "bf-copied-badge" : ""}`}
                                    onClick={() => handleCopyKey(item.licenseKey)}
                                    title={isCopied ? "Copied to clipboard!" : "Copy Key"}
                                  >
                                    {isCopied ? <Check size={13} /> : <Copy size={13} />}
                                  </button>
                                </div>
                              </td>
                              <td>
                                <span className="tech-badge neutral">
                                  {item.plan ? `${item.plan}D` : "—"}
                                </span>
                              </td>
                              <td>
                                <span
                                  className="font-mono"
                                  style={{
                                    fontSize: "12px",
                                    color: expired ? "var(--red)" : "var(--text-1)",
                                  }}
                                >
                                  {formatISTDateTime(item.expiryDate)}
                                </span>
                              </td>
                              <td>
                                <select
                                  className="bf-status-select"
                                  value={item.hasUsed ? "true" : "false"}
                                  onChange={(e) =>
                                    handleHasUsedChange(item._id, e.target.value === "true")
                                  }
                                >
                                  <option value="false">false</option>
                                  <option value="true">true</option>
                                </select>
                              </td>
                              <td>
                                <span className="tech-badge neutral">
                                  <Tag size={10} />
                                  {item.payment || "NONE"}
                                </span>
                              </td>
                              <td>
                                <span className="font-mono" style={{ fontSize: "12px", color: "var(--text-2)" }}>
                                  {item.genBy}
                                </span>
                              </td>
                              <td>
                                <span className="font-mono" style={{ fontSize: "11.5px", color: "var(--text-3)" }}>
                                  {formatISTDateTime(item.genTime)}
                                </span>
                              </td>
                              <td style={{ textAlign: "right" }}>
                                <button
                                  className="bf-btn-delete"
                                  onClick={() => handleDeleteLicense(item._id)}
                                  title="Delete Key Record"
                                >
                                  <Trash2 size={13} />
                                  <span>DELETE</span>
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })
        )}
      </section>
    </div>
  );
};

export default Licenses;
